import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';

const MATH_SYMBOLS = ['π', '∑', '∞', '√', 'x²', '÷', '+', '−', '×', '=', 'θ', 'Δ'];

export default function WelcomePage() {
  const navigate = useNavigate();
  const [hoveredBtn, setHoveredBtn] = useState(null);

  const floatingSymbols = useMemo(() =>
    MATH_SYMBOLS.map((s, i) => ({
      symbol: s,
      left: `${(i * 8.3) % 100}%`,
      delay: `${i * 1.2}s`,
      duration: `${8 + (i % 5) * 2}s`,
      size: `${1.2 + (i % 3) * 0.6}rem`,
    })), []);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/home';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col items-center justify-center px-4" style={{ background: '#FFF8F0' }}>
      {/* Floating math symbols */}
      {floatingSymbols.map((s, i) => (
        <span key={i} className="math-symbol animate-float-up" style={{
          left: s.left, animationDelay: s.delay, animationDuration: s.duration, fontSize: s.size
        }}>{s.symbol}</span>
      ))}

      <div className="relative z-10 w-full max-w-md text-center">
        {/* Logo */}
        <div className="mb-6 animate-bounce-in">
          <img src="assets/logo.png" alt="MathTopper Logo" className="mx-auto mb-2" style={{ width: '60px', height: '60px' }} />
          <h1 className="font-fredoka text-4xl md:text-5xl tracking-tight" style={{ color: '#FF6B35' }}>
            MathTopper
          </h1>
          <p className="text-base md:text-lg font-bold mt-2" style={{ color: '#1B4F8A' }}>
            India's Favourite Daily Math App
          </p>
        </div>

        {/* Tagline */}
        <p className="text-sm font-semibold mb-8" style={{ color: '#8B7355' }}>
          Daily practice. Streaks. AI tutor. Fun!
        </p>

        {/* Role Selection Cards */}
        <div className="space-y-4 mb-6">
          <button
            data-testid="student-role-btn"
            onClick={() => navigate('/signup/student')}
            onMouseEnter={() => setHoveredBtn('student')}
            onMouseLeave={() => setHoveredBtn(null)}
            className="btn-press w-full bg-white border-2 rounded-3xl p-5 flex items-center gap-4 text-left"
            style={{
              borderColor: hoveredBtn === 'student' ? '#FF6B35' : '#E5DACE',
              boxShadow: '0 4px 0 #E5DACE',
              transform: hoveredBtn === 'student' ? 'translateY(-2px)' : 'none',
            }}
          >
            <span className="text-4xl">🎓</span>
            <div>
              <p className="text-lg font-extrabold" style={{ color: '#1B4F8A' }}>I'm a Student</p>
              <p className="text-sm font-semibold" style={{ color: '#8B7355' }}>Class 6-10, start practicing!</p>
            </div>
          </button>

          <button
            data-testid="parent-role-btn"
            onClick={() => navigate('/signup/parent')}
            onMouseEnter={() => setHoveredBtn('parent')}
            onMouseLeave={() => setHoveredBtn(null)}
            className="btn-press w-full bg-white border-2 rounded-3xl p-5 flex items-center gap-4 text-left"
            style={{
              borderColor: hoveredBtn === 'parent' ? '#1B4F8A' : '#E5DACE',
              boxShadow: '0 4px 0 #E5DACE',
              transform: hoveredBtn === 'parent' ? 'translateY(-2px)' : 'none',
            }}
          >
            <span className="text-4xl">👨‍👧</span>
            <div>
              <p className="text-lg font-extrabold" style={{ color: '#1B4F8A' }}>I'm a Parent</p>
              <p className="text-sm font-semibold" style={{ color: '#8B7355' }}>Monitor your child's progress</p>
            </div>
          </button>
        </div>

        {/* Google Login */}
        <button
          data-testid="google-login-btn"
          onClick={handleGoogleLogin}
          className="btn-press w-full bg-white border-2 rounded-full py-3 px-6 flex items-center justify-center gap-3 font-bold text-sm mb-4"
          style={{ borderColor: '#E5DACE', color: '#1B4F8A', boxShadow: '0 3px 0 #E5DACE' }}
        >
          <Sparkles size={18} style={{ color: '#FFD23F' }} />
          Sign in with Google
        </button>

        {/* Already have account */}
        <button
          data-testid="login-link-btn"
          onClick={() => navigate('/login')}
          className="text-sm font-bold underline underline-offset-4"
          style={{ color: '#1B4F8A' }}
        >
          Already have an account? Login
        </button>

        {/* Teacher/Admin link */}
        <div className="mt-4 flex items-center justify-center gap-3">
          <button
            data-testid="teacher-signup-link"
            onClick={() => navigate('/signup/teacher')}
            className="text-xs font-semibold"
            style={{ color: '#8B7355' }}
          >
            Teacher? Sign up
          </button>
          <span className="text-xs text-slate-300">|</span>
          <button
            data-testid="teacher-login-link"
            onClick={() => navigate('/login')}
            className="text-xs font-semibold"
            style={{ color: '#8B7355' }}
          >
            Teacher/Admin Login
          </button>
        </div>
      </div>
    </div>
  );
}
